<template>
  <div
    data-ui="campaign-details"
  >
    <HeroComponent
      v-if="shouldShowCallToAction === false"
      v-bind="heroContent"
      :banner-image-src="bannerImageSrc"
      :screen-width="screenWidth"
      :submission-complete="submissionComplete"
      data-ui="campaign-details-hero"
    />
    <StatsComponent
      v-if="campaignStatsData"
      v-bind="campaignStatsData"
      data-ui="campaign-details-stats"
    />
    <div>
      <div class="campaign-details__content can-container">
        <CdrLink
          v-if="shouldShowCallToAction === false"
          href="/action/network"
          class="campaign-details__breadcrumb"
          modifier="standalone"
          data-ui="campaign-details-breadcrumb"
          @click="handleClick"
        >
          <IconCaretLeft
            alt=""
            width="21"
            height="36"
          />
          Cooperative Action Network
        </CdrLink>
        <div
          id="campaign-details-campaign-content"
          :class="containerClasses"
          :data-ui="wrapperDataUi"
        >
          <LongFormComponent
            v-if="campaignDetails"
            :long-form-content="campaignDetails"
            :heading="campaignHeading"
            :resource-sections="resourceSections"
            :graphql-enabled="graphQLEnabled"
            data-ui="campaign-copy"
          />
          <template v-if="shouldShowCallToAction">
            <div
              class="post-signage-thank-you"
              data-ui="post-signage-thank-you"
            >
              <CdrImg
                alt="two congratulatory camping mugs clinking together"
                src="https://www.rei.com/dam/19254152_cooperative-action-mugs.png"
                aria-hidden="true"
              />
              <div
                v-if="showMembershipPromoTest"
              >
                <CdrText
                  tag="h2"
                  class="campaign-post-signage-messaging__heading"
                >
                  You did it!
                </CdrText>
                <CdrText
                  tag="h2"
                  class="campaign-post-signage-messaging__heading"
                >
                  Thanks for doing your part.
                </CdrText>
              </div>
              <CdrText
                v-else
                tag="h2"
                class="campaign-post-signage-messaging__heading"
              >
                You did it! Now inspire others.
              </CdrText>
              <CdrText class="campaign-post-signage-messaging__subheading">
                {{ subheading }}
              </CdrText>
              <ShareButtons
                v-if="showMembershipPromoTest"
                :page-name="'campaign-post-signage'"
                :share-cta="shareCta"
                :center-buttons="true"
              />
              <MembershipPromo
                v-if="showMembershipPromoTest"
              />
            </div>
            <div
              v-if="!showMembershipPromoTest"
              class="campaign-post-signage-messaging"
            >
              <CdrImg
                alt="two hearts symbolic of sharing"
                src="https://www.rei.com/dam/19254144_cooperative-action-hearts.png"
                aria-hidden="true"
              />
              <CdrText
                tag="h2"
                class="campaign-post-signage-messaging__heading"
              >
                Double Your Impact!
              </CdrText>
              <CdrText class="campaign-post-signage-messaging__subheading">
                Invite a friend, family member, and everyone in between to take action.
              </CdrText>
            </div>
          </template>
          <EmbedComponent
            v-if="!submissionComplete || (submissionComplete && !showMembershipPromoTest)"
            :campaign-details="campaignDetails"
            :form-id="formId"
            :is-advocacy="isAdvocacyForm"
            :show-privacy-policy="showPrivacyPolicy && !showMembershipPromoTest"
            :data-ui="'campaign-form'"
            :show-information-banner="!submissionComplete"
          />
          <template v-if="shouldShowCallToAction">
            <div
              v-if="!showMembershipPromoTest"
              class="campaign-post-signage-messaging post-signage-cta-further-action"
              data-ui="post-signage-cta-further-action"
            >
              <CdrImg
                alt="a bugle or megaphone representing a call to action"
                src="https://www.rei.com/dam/19254154_cooperative-action-bugle.png"
                aria-hidden="true"
              />
              <CdrText
                tag="h2"
                class="campaign-post-signage-messaging__heading"
              >
                Your voice has power
              </CdrText>
              <CdrText class="campaign-post-signage-messaging__subheading">
                Harness your power to protect and build a better outdoors for all.
              </CdrText>
              <CdrButton
                modifier="secondary"
                tag="a"
                href="/action/network#takeAction"
                @click="onClickTakeAction"
              >
                Take Action
              </CdrButton>
            </div>
            <AdditionalCampaignsComponent
              v-if="additionalCampaigns"
              v-bind="additionalCampaigns"
            />
          </template>
        </div>
        <cdr-text
          v-if="submissionComplete && showMembershipPromoTest"
          class="campaign-post-signage-footnote"
        >
          <sup>†</sup>REI’s donations are made to a “Designated Fund” that’s been established
          at the Seattle Foundation (Tax ID#91-6013536) for the benefit of the REI
          Cooperative Action Fund. Contributions to that Designated Fund are
          treated for tax purposes as gifts to a Section 501(c)(3) public charity.
        </cdr-text>
      </div>
    </div>

    <template v-if="!showMembershipPromoTest">
      <div class="campaign-post-signage-messaging post-signage-learning can-container">
        <CdrText
          tag="h1"
          class="post-signage-learning__heading"
        >
          What is the REI <br> Cooperative Action Network?
        </CdrText>
        <CdrButton
          tag="a"
          href="/action"
          @click="onClickLearnMore"
        >
          Learn More
        </CdrButton>
      </div>
    </template>
  </div>
</template>

<script>
import {
  CdrButton, CdrImg, CdrLink, CdrText, IconCaretLeft,
} from '@rei/cedar';
import { win } from '@rei/browser-shim';
import analyticsMixin from '../../../common/mixins/analyticsMixin';
import EmbedComponent from '../../../common/components/EmbedComponent.vue';
import ShareButtons from '../../../common/components/ShareButtonsComponent.vue';
import StatsComponent from './components/StatsComponent.vue';
import HeroComponent from '../../../common/components/HeroComponent.vue';
import LongFormComponent from './components/LongFormComponent.vue';
import MembershipPromo from '../components/MembershipPromo.vue';
import fixPostalCode from '../../../utils/embed-accessibility-fixes';
import { getHeroModel } from '../../../utils/common-model-adapter';
import {
  getAdditionalCampaignsModel,
  getAdvocacyFormBool,
  getCampaignFormId,
  transformAdditionalCampaignsModel,
} from '../../../utils/cooperative-action-homepage-model-adapter';
import { getCampaignStatsModel, transformHeroModel } from '../../../utils/cooperative-action-campaigns-model-adapter';
import AdditionalCampaignsComponent from '../components/AdditionalCampaignsComponent.vue';

export default {
  name: 'CampaignDetailsPageComponent',
  components: {
    CdrButton,
    CdrImg,
    CdrText,
    CdrLink,
    IconCaretLeft,
    LongFormComponent,
    EmbedComponent,
    HeroComponent,
    StatsComponent,
    ShareButtons,
    MembershipPromo,
    AdditionalCampaignsComponent,
  },
  mixins: [
    analyticsMixin,
  ],
  props: {
    additionalData: { type: Object, default: () => ({}) },
    componentModels: { type: Object, default: () => ({}) },
    graphQLEnabled: { type: Boolean, default: false, required: false },
  },
  data() {
    return {
      analyticsCaptureTimer: '',
      errorAnalyticsSent: false,
      postalCodeFieldAccesibilityFixComplete: false,
      rawErrorCodes: [],
      screenWidth: 0,
      showPrivacyPolicy: true,
      submissionComplete: false,
      showCallToAction: false,
      formId: '',
    };
  },
  computed: {
    analyticsViewData() {
      let pageTitle = '';
      if (this.graphQLEnabled) {
        pageTitle = this.componentModels?.hero?.heading;
      } else {
        const { 'campaign-info': campaign } = this.componentModels;
        const { headings } = campaign;
        const { pageTitle: pageTitleObj } = headings;
        ({ text: pageTitle } = pageTitleObj);
      }

      return {
        siteId: 'cooperative-action',
        templateType: 'campaign-details',
        pageSection: pageTitle,
      };
    },
    isAdvocacyForm() {
      return getAdvocacyFormBool(this?.componentModels['campaign-info']);
    },
    shouldShowCallToAction() {
      return this.submissionComplete && this.showCallToAction;
    },
    wrapperDataUi() {
      return this.shouldShowCallToAction ? 'wrapper-post-submission' : '';
    },
    containerClasses() {
      return {
        'campaign-details__campaign-content': true,
        'hidden-every-action-thank-you-message': this.shouldShowCallToAction,
        'campaign-details__campaign-content--form-submitted': this.shouldShowCallToAction,
      };
    },
    campaignProgressData() {
      return this.additionalData?.campaignDetailsMap || null;
    },
    campaignStatsData() {
      return getCampaignStatsModel(this.componentModels?.['campaign-stats']) || null;
    },
    heroContent() {
      if (this.graphQLEnabled) {
        return transformHeroModel(this.componentModels?.hero, this.campaignProgressData) || {};
      }
      const hero = this.componentModels?.['campaign-hero'] || this.componentModels?.['campaign-info'];
      return getHeroModel(hero, this.campaignProgressData);
    },
    resourceSections() {
      return this.graphQLEnabled
        ? this.componentModels?.['campaign-resources']?.resources || []
        : this.componentModels?.['campaign-resources']?.contentCollectionElements || [];
    },
    /*
      "model" props
    */
    bannerImageSrc() {
      const { bannerImageSrc = '' } = this.campaignStatsData || {};
      return bannerImageSrc;
    },
    campaignDetails() {
      if (!this.componentModels?.['campaign-details'] || (this.shouldShowCallToAction)) return null;
      const { 'campaign-details': details } = this.componentModels;
      const { markup = null } = details;
      return markup;
    },
    campaignHeading() {
      if (!this.componentModels?.['campaign-details'] || (this.shouldShowCallToAction)) return null;
      const { 'campaign-details': details } = this.componentModels;
      const { heading = null } = details;
      return heading;
    },
    additionalCampaigns() {
      return {
        ...(this.graphQLEnabled
          ? transformAdditionalCampaignsModel(this.componentModels?.['additional-campaigns']) || null
          : getAdditionalCampaignsModel(this.componentModels?.['additional-campaigns']) || null),
        showMembershipPromoTest: this.showMembershipPromoTest,
      };
    },
    showMembershipPromoTest() {
      return this.additionalData?.['can.membership.promo.test'];
    },
    subheading() {
      return this.showMembershipPromoTest
        ? 'You’re part of the collective that’s fighting for life outside. Double your impact by '
        + 'spreading awareness—invite friends and family to join you in taking action!'
        : "It may feel like a small step, but it isn't. You're part of the community that "
        + 'is fighting for life outside.';
    },
  },
  mounted() {
    this.onResize();
    this.formId = getCampaignFormId(this?.componentModels['campaign-info']);
    // Add onSubmit callback to EveryAction form (ActionTag component)
    // https://docs.everyaction.com/reference/callbacks
    // eslint-disable-next-line
    if (win) {
      win.addEventListener('resize', this.onResize);

      // eslint-disable-next-line camelcase
      const nvtag_callbacks = win.nvtag_callbacks || {};
      const onSubmitCallback = this.handleSubmit;
      // eslint-disable-next-line camelcase
      nvtag_callbacks.onSubmit = nvtag_callbacks.onSubmit || [];
      // eslint-disable-next-line camelcase
      nvtag_callbacks.onSubmit.push(onSubmitCallback);

      const postRenderCallback = this.handlePostRender;
      // eslint-disable-next-line camelcase
      nvtag_callbacks.postRender = nvtag_callbacks.postRender || [];
      // eslint-disable-next-line camelcase
      nvtag_callbacks.postRender.push(postRenderCallback);

      const onSegueCallback = this.handleSegue;
      // eslint-disable-next-line camelcase
      nvtag_callbacks.segue = nvtag_callbacks.segue || [];
      // eslint-disable-next-line camelcase
      nvtag_callbacks.segue.push(onSegueCallback);

      // eslint-disable-next-line camelcase
      win.nvtag_callbacks = nvtag_callbacks;
    }
  },
  beforeDestroy() {
    // eslint-disable-next-line
    win && win.removeEventListener('resize', this.onResize);

    Array.from(document.querySelectorAll('.container .embed button'))
      .forEach((elem) => elem.removeEventListener('click', this.handleSocialShareClick));
  },
  methods: {
    onClickTakeAction() {
      if (window && window.metrics) {
        window.metrics.link(`cooperative-action:campaign details_${this.heroContent}_take action`);
      }
    },
    onClickLearnMore() {
      if (window && window.metrics) {
        window.metrics.link(`cooperative-action:campaign details_${this.heroContent}_learn more`);
      }
    },
    onResize() {
      this.screenWidth = win.innerWidth || 0;
    },
    handleSubmit() {
      if (window && window.metrics) {
        window.metrics.link(`cooperative-action:campaign details_${this.heroContent}_form info submitted`);
      }
      this.showPrivacyPolicy = true;
    },
    handleSocialShareClick({ target }) {
      if (window && window.metrics) {
        window.metrics.link(`cooperative-action:campaign details_${this.heroContent}_social_share_${target.innerText}`);
      }
    },
    handleClick() {
      if (window && window.metrics) {
        window.metrics.link(`cooperative-action:campaign details_${this.heroContent}_breadcrumb_Cooperative Action Network`);
      }
    },
    handlePostRender() {
      // removes `autocomplete="postal-code"` from input[type="tel"] used for the postal code
      const { postalCodeFieldAccesibilityFixComplete } = this;
      if (!postalCodeFieldAccesibilityFixComplete) {
        this.postalCodeFieldAccesibilityFixComplete = fixPostalCode();
      }

      // IMP-727 remove invalid aria attribute (EA will not be fixing soon)
      setTimeout(() => {
        const flagElement = document.querySelector('.iti__selected-flag');
        if (flagElement && flagElement.getAttribute('aria-activedescendant')) {
          flagElement.removeAttribute('aria-activedescendant');
        }
      }, 500);
    },
    handleSegue(args) {
      if (args && args.thank) {
        this.submissionComplete = true;
        this.showCallToAction = true;

        // add event listeners for clicks on social share buttons returned from EveryAction
        Array.from(document.querySelectorAll('.container .embed button'))
          .forEach((elem) => elem.addEventListener('click', (event) => this.handleSocialShareClick(event)));

        const topOfPage = document.getElementById('app');
        if (topOfPage) {
          topOfPage.setAttribute('tabindex', '-1');
          topOfPage.focus();
          if (window) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          }
        }
      }
    },
  },
};
</script>

<style lang="scss">

.can-container {
  @include cdr-container;
}

.campaign-details__content {
  padding: $cdr-space-inset-four-x $cdr-space-inset-one-x $cdr-space-inset-three-x;
  min-height: 50vh; // CLS fix/hack
}

.campaign-details__campaign-content {
  width: 100%;

  @include cdr-sm-mq-up {
    display: grid;
    grid-auto-flow: column;
    grid-column-gap: $cdr-space-one-x;
    grid-template-columns: 1fr 1fr;
  }
}

.campaign-details__campaign-content--form-submitted {
  @include cdr-sm-mq-up {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }

  .embed {
    /*
    * This query/grid handles organizing the social media share buttons
    * returned from EveryAction on DESKTOP widths
    */
    @media screen and (min-width: $cdr-breakpoint-sm) {
      grid-row: 3;
      grid-column: 1;
    }
  }
}

.campaign-details__breadcrumb {
  display: inline-flex;
  color: $cdr-color-text-secondary;
  margin-bottom: $cdr-space-one-x;
  svg {
    margin-left: -7px;
    fill: $cdr-color-text-secondary;
  }

  &:hover svg {
      fill: $cdr-color-text-link-hover;
  }
  &:focus svg {
      fill: $cdr-color-text-link-active;
  }
}

header.at-title.at-title {
  @include cdr-text-heading-sans-500;
  padding: 0;
  text-align: left;
  margin-bottom: $cdr-space-one-x;
}

.ngp-form.ngp-form {
  max-width: initial;
}

.at-markup.HeaderHtml {
  padding: 0;
  padding-bottom: $cdr-space-two-x;
  margin-bottom: $cdr-space-two-x;
  @include cdr-sm-mq-down {
    border-bottom: 1px solid rgba(66, 59, 47, 0.75);
  }
}

.at-markup p {
  @include cdr-text-body-300;
}

.at-markup ul {
  padding-left: 2rem;
}

.at-markup h3 {
  @include cdr-text-body-400;
}

.at-markup h3 strong {
  @include cdr-text-body-strong-400;
}

.at.at .at-fieldset {
  padding: 0;
}

.at.at .at-targets {
  background-color: $can-background-natural;
  padding: $cdr-space-one-x;
  border-radius: $cdr-radius-softer;

  .at-targets-have-images > figure {
    margin: 0;
  }

  .at-target-sent {
    margin-right: $cdr-space-three-quarter-x;

    &:before {
      background-color: $cdr-color-text-success;
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffffff'%3E%3Cpath role='presentation' d='M9.673 18.669h.001L19.766 6.644a1 1 0 10-1.532-1.286l-9.3 11.085-3.169-3.776a1 1 0 10-1.532 1.286l3.875 4.618a.999.999 0 001.565.099z'/%3E%3C/svg%3E");
      background-size: 14px 14px;
      background-position: 2px 2px;
      content: "";
    }
  }
}

.at .at-legend {
  padding: 0;
  margin: $cdr-space-two-x 0 $cdr-space-one-x;
  @include cdr-text-body-strong-300;
}

.at-fields .at-row label {
  padding: 0 0 $cdr-space-one-x;
}

.at.at .at-fieldset.AdvocacyFields {
  padding-top: $cdr-space-one-x;
  padding-top: $cdr-space-two-x;
  border-top: 1px solid rgba(66, 59, 47, 0.75);

  .at-legend {
    display: none;
  }
}

.at.at textarea{
  margin-top: $cdr-space-half-x;
  @include cdr-text-body-300
}

.at.at .at-area {
  padding-bottom: $cdr-space-two-x;
}

.at-row .Outro0 {
  padding-bottom: $cdr-space-two-x;
}

.at.at .at-form-submit {
  padding-bottom: $cdr-space-two-x;
}

.at.at .at-text.PersonalUrl {
  display: none;
}

.at.at input[type=checkbox]+span {
  span {
    margin-left: $cdr-space-half-x;
  }
}

.at .at-row.FirstName {
  column-gap: 1.8rem;
}

@include cdr-md-mq-down {
  .at .at-row.Prefix,
  .at .at-row.PostalCode,
  .at .at-row.EmailAddress {
    display: block;
  }
}

@include cdr-md-mq-up {
  .at .at-row.Prefix {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 1.8rem;
  }

  .at .at-row.EmailAddress {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 1.8rem;
  }

  .at .at-row.PostalCode {
    column-gap: 1.8rem;
  }

  .at .at-text.Prefix {
    grid-column-start: 1;
    grid-column-end: 2;
    grid-row-start: 1;
    grid-row-end: 2;
    grid-column: 1;
  }

  .at .at-text.FirstName {
    grid-column-start: 2;
    grid-column-end: 5;
    grid-row-start: 1;
    grid-row-end: 2;
  }

  .at .at-text.LastName {
    grid-column-start: 1;
    grid-column-end: 5;
    grid-row-start: 2;
    grid-row-end: 2;
  }

  .at .at-select.StateProvince {
    grid-column-start: 1;
    grid-column-end: 2;
    grid-row-start: 2;
    grid-row-end: 2;
  }
}

.at-form .at-inner {
  background-color: transparent;
}

.at-inner {
  @include cdr-sm-mq-up {
    display: grid;
    grid-auto-columns: 1fr 1fr;
    grid-column-gap: $cdr-space-two-x;

    // move all non-copy content in the embed to the right
    & > * {
      grid-row: 1;
      grid-column: 2;
    }

    form {
      grid-row: 2 / 5;
    }

    .at-title {
      grid-row: 2;
      grid-column: 1;
    }
    .at-markup {
      grid-row: 3;
      grid-column: 1;
    }
  }
}

// removes the header & footer elements, which are empty but still present in the DOM
footer.at-markup {
  display: none;
}

.at .contributions {
  text-align: center;
  max-width: 343px;
  margin: 0 auto;

  @include cdr-sm-mq-up {
    max-width: 471px;
  }

  @include cdr-md-mq-up {
    max-width: 568px;
  }

  p {
    margin-bottom: 0;
  }

  .confirmation__image {
    width: 120px;
    margin: $cdr-space-half-x auto $cdr-space-two-x;
  }

  .confirmation__heading {
    @include cdr-text-heading-serif-700;
  }

  .confirmation__delivery-notification {
    @include cdr-text-body-300;
    margin-bottom: $cdr-space-two-x;
    width: 100%;
  }

  .confirmation__share-prompt {
    @include cdr-text-utility-sans-200;
    margin-bottom: $cdr-space-one-x;
  }

  .btn-facebook {
    margin-right: $cdr-space-half-x;
    margin-bottom: $cdr-space-two-x;
  }

  .btn-twitter {
    margin-left: $cdr-space-half-x;
    margin-bottom: $cdr-space-two-x;
  }
}

.confirmation__back-link {
  display: block;
  text-align: center;
  margin: $cdr-space-four-x;
}

.hidden-every-action-thank-you-message {
  #campaign-signage-thank-you-text {
    display: none;
  }
}

.embed {
  .content button {
    margin-left: -25px;
  }
}

.embed__privacy-notice {
  margin: 0 auto $cdr-space-two-x;
  @include cdr-md-mq-up {
    margin: $cdr-space-one-x 0 $cdr-space-one-x auto;
    text-align: left;
  }
}

.campaign-post-signage-messaging img {
  margin-bottom: $cdr-space-one-x;
}

.campaign-post-signage-messaging {
    text-align: center;
    grid-row: 2;
}

.campaign-post-signage-footnote {
  @include cdr-text-utility-sans-200;
  margin-top: $cdr-space-two-x;
  sup {
      line-height: 1.2rem;
  }
}

.campaign-post-signage-messaging__heading.campaign-post-signage-messaging__heading {
    @include cdr-text-heading-sans-500;
    margin-bottom: $cdr-space-half-x;
}

.campaign-post-signage-messaging__subheading.campaign-post-signage-messaging__subheading {
    @include cdr-text-body-300;
    max-width: 320px;
    margin: auto;
    margin-bottom: $cdr-space-one-x;
}

.post-signage-thank-you {
    text-align: center;
    img {
        margin-bottom: $cdr-space-one-x;
    }

    .campaign-post-signage-messaging__heading {
      @include cdr-text-heading-sans-600;
    }

    .campaign-post-signage-messaging__subheading{
      max-width: 475px;
    }

    @media screen and (min-width: $cdr-breakpoint-sm) {
        grid-column: 1/3;
    }
}

.post-signage-cta-further-action {
    @media screen and (min-width: $cdr-breakpoint-sm) {
        border-left: solid 1px $cdr-color-border-primary;
        grid-row: 2/4;
    }

    .campaign-post-signage-messaging__subheading {
        min-height: 18px;
    }
}

.post-signage-learning {
    @include cdr-text-heading-serif-800;
    background-color: $cdr-color-background-message-default-02;
    margin: $cdr-space-two-x auto $cdr-space-two-x;
    padding: $cdr-space-three-x;
    position: relative;
    width: 100%;

    h1 {
      margin: auto;
    }
    @media screen and (min-width: $cdr-breakpoint-sm) {
        grid-column: 1/3;
        grid-row: 5;
    }
}

.post-signage-learning__heading.post-signage-learning__heading {
    @include cdr-text-heading-serif-800;
    margin-bottom: $cdr-space-one-x;
    color: $cdr-color-text-brand;
}

// IMP-1271 Email typo warning text styling
.embed .didYouMean {
  color: $cdr-color-text-message-error !important;
  font-weight: 500 !important;
  a {
    color: $cdr-color-text-message-error;
    font-weight: 500;
    text-decoration: none !important;
  }
}
</style>
